import Link from 'next/link';
import styled from 'styled-components';

import { FeatureFlags } from 'featureFlags/constants';
import { t } from 'localization/utils/t';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { variableFontRegular } from 'styles/theme/text';
import { Window } from 'shared/components/Window';

const TP = 'giftCards.components.GiftCardsFactsWindow';

interface IGiftCardsFactsWindowProps {
  initialWindowPosition?: { bottom: number; left: number };
  isPermanent: boolean;
  handleClose: () => void;
  handleClick?: () => void;
}

export const GiftCardsFactsWindow = ({
  initialWindowPosition,
  isPermanent,
  handleClick,
  handleClose,
}: IGiftCardsFactsWindowProps) => {
  const isIOSRedemptionEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_GIFT_CARD_REDEMPTION_TEXT_IOS,
  );
  const isAndroidRedemptionEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_GIFT_CARD_REDEMPTION_TEXT_ANDROID,
  );

  const getFactsWindowText = () => {
    const enabledMobileDevices: string[] = [];
    if (isIOSRedemptionEnabled) {
      enabledMobileDevices.push('iOS');
    }
    if (isAndroidRedemptionEnabled) {
      enabledMobileDevices.push('Android');
    }

    const baseText =
      'GOAT Gift Cards are gift cards delivered via email. Gift cards are available for purchase online and can be redeemed on GOAT.com {redemptionOptions}.';

    switch (enabledMobileDevices.length) {
      case 2:
        return t(`${TP}.factsWindowText`, baseText, { redemptionOptions: 'or in the GOAT app' });
      case 1:
        return t(`${TP}.factsWindowText`, baseText, {
          redemptionOptions: `or in the GOAT app (${enabledMobileDevices[0]} only)`,
        });
      default:
        return t(`${TP}.factsWindowText`, baseText, { redemptionOptions: 'only' });
    }
  };

  return (
    <Window
      id="facts"
      isPdp={false}
      isPermanent={isPermanent}
      onClick={handleClick}
      onClose={handleClose}
      title={t(`${TP}.title`, 'Facts')}
      width="335px"
      initialPermanentPosition={initialWindowPosition}
    >
      <GiftCardsFactsWindow.Text>{getFactsWindowText()}</GiftCardsFactsWindow.Text>
      <GiftCardsFactsWindow.NavLink data-qa="gift_cards_terms_and_conditions">
        <Link
          href="/gift-card-terms"
          passHref
          prefetch={false}
          target="_blank"
          rel="noreferrer noopener"
        >
          {t(`${TP}.terms`, 'Gift Card Terms')}
        </Link>
      </GiftCardsFactsWindow.NavLink>
    </Window>
  );
};

GiftCardsFactsWindow.Text = styled.span`
  margin: 0px 16px 16px 16px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;

GiftCardsFactsWindow.NavLink = styled.div`
  margin: 16px 16px 24px 16px;
  cursor: pointer;
  text-decoration: underline;

  > a {
    ${variableFontRegular({ size: '11.5px', height: '14px' })};

    @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
      ${variableFontRegular({ size: '11px', height: '14px' })};
    }
  }

  > button {
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }
`;
